import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../services/AuthContext';
import Navbar from '../components/Navbar';
import '../styles/Messages.css';

function getConvoName(convo, currentUserId) {
  if (convo.is_group) {
    if (convo.name) return convo.name;
    const others = (convo.conversation_members || [])
      .filter(m => m.user_id !== currentUserId)
      .map(m => m.profiles?.full_name)
      .filter(Boolean);
    return others.join(', ') || 'Group Chat';
  }
  const other = (convo.conversation_members || []).find(m => m.user_id !== currentUserId);
  return other?.profiles?.full_name || 'Unknown User';
}

function getConvoAvatar(convo, currentUserId) {
  if (convo.is_group) return null;
  const other = (convo.conversation_members || []).find(m => m.user_id !== currentUserId);
  return other?.profiles?.avatar_url || null;
}

function NewDMModal({ friends, onClose, onCreated }) {
  const [selectedId, setSelectedId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedId) return;
    setSubmitting(true);
    setError('');
    try {
      const res = await api.post('/messages/conversations', { member_ids: [selectedId] });
      onCreated(res.data.conversation.id);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to start conversation');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-box">
        <h3>New Direct Message</h3>
        {error && <div className="error-message">{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Send to</label>
            <select
              value={selectedId}
              onChange={e => setSelectedId(e.target.value)}
              style={{ width: '100%', padding: '0.625rem 0.875rem', border: '1px solid var(--border)', borderRadius: 'var(--radius)', fontSize: '0.875rem', background: 'var(--surface)', color: 'var(--text-primary)' }}
            >
              <option value="">Choose a friend…</option>
              {friends.map(f => (
                <option key={f.id} value={f.id}>{f.full_name}</option>
              ))}
            </select>
          </div>
          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={!selectedId || submitting}>Start Chat</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function NewGroupModal({ friends, onClose, onCreated }) {
  const [groupName, setGroupName] = useState('');
  const [selected, setSelected] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const toggleFriend = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selected.length === 0) { setError('Select at least one friend'); return; }
    setSubmitting(true);
    setError('');
    try {
      const res = await api.post('/messages/conversations', {
        member_ids: selected,
        name: groupName.trim() || null,
        is_group: true
      });
      onCreated(res.data.conversation.id);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create group');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-box">
        <h3>New Group Chat</h3>
        {error && <div className="error-message">{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Group name (optional)</label>
            <input
              type="text"
              value={groupName}
              onChange={e => setGroupName(e.target.value)}
              placeholder="e.g. Weekend plans"
            />
          </div>
          <div className="form-group">
            <label>Add friends</label>
            <div className="friend-checkbox-list">
              {friends.map(f => (
                <label key={f.id} className="friend-checkbox-item">
                  <input
                    type="checkbox"
                    checked={selected.includes(f.id)}
                    onChange={() => toggleFriend(f.id)}
                  />
                  <span>{f.full_name}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={selected.length === 0 || submitting}>Create Group</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Messages() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [friends, setFriends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDMModal, setShowDMModal] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);

  const currentUserId = user?.user?.id || user?.id;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [convosRes, friendsRes] = await Promise.all([
          api.get('/messages/conversations'),
          api.get('/friends/')
        ]);
        setConversations(convosRes.data.conversations);
        setFriends(friendsRes.data.friends);
      } catch {}
      finally { setLoading(false); }
    };
    fetchData();
  }, []);

  const handleConvoCreated = (id) => {
    navigate(`/messages/${id}`);
  };

  const formatLastMessage = (convo) => {
    if (!convo.last_message) return 'No messages yet';
    const sender = convo.last_message.profiles?.full_name?.split(' ')[0];
    const preview = convo.last_message.content?.slice(0, 60);
    return sender ? `${sender}: ${preview}` : preview;
  };

  return (
    <div className="page-container">
      <Navbar />
      <div className="messages-container">
        <div className="messages-content">
          <div className="messages-page-header">
            <h2>Messages</h2>
            {friends.length > 0 && (
              <div className="messages-page-actions">
                <button className="btn-secondary" onClick={() => setShowDMModal(true)}>+ Direct Message</button>
                <button className="btn-primary" onClick={() => setShowGroupModal(true)}>+ Group</button>
              </div>
            )}
          </div>

          {loading ? (
            <div className="loading">Loading…</div>
          ) : conversations.length === 0 ? (
            <div className="messages-empty">
              <p>No conversations yet. Send a message to a friend!</p>
            </div>
          ) : (
            <div className="conversation-list">
              {conversations.map(convo => {
                const name = getConvoName(convo, currentUserId);
                const avatarUrl = getConvoAvatar(convo, currentUserId);
                return (
                  <button
                    key={convo.id}
                    className="conversation-item"
                    onClick={() => navigate(`/messages/${convo.id}`)}
                  >
                    <div className={`convo-avatar${convo.is_group ? ' group-avatar' : ''}`}>
                      {!convo.is_group && avatarUrl
                        ? <img src={avatarUrl} alt={name} />
                        : convo.is_group ? '👥' : name.charAt(0).toUpperCase()
                      }
                    </div>
                    <div className="convo-info">
                      <span className="convo-name">{name}</span>
                      <span className="convo-last-message">{formatLastMessage(convo)}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {showDMModal && (
        <NewDMModal
          friends={friends}
          onClose={() => setShowDMModal(false)}
          onCreated={handleConvoCreated}
        />
      )}
      {showGroupModal && (
        <NewGroupModal
          friends={friends}
          onClose={() => setShowGroupModal(false)}
          onCreated={handleConvoCreated}
        />
      )}
    </div>
  );
}

export default Messages;
